import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import { Checkbox } from '../../../components/ui/Checkbox';
import dashboardCustomizationService from '../../../services/dashboardCustomizationService';

const FilteredViewCreator = ({ targetEntity, onSave, onClose }) => {
  const [viewName, setViewName] = useState('');
  const [viewDescription, setViewDescription] = useState('');
  const [filters, setFilters] = useState([]);
  const [isShared, setIsShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const filterOperators = [
    { value: 'equals', label: 'Equals' },
    { value: 'not_equals', label: 'Not Equals' },
    { value: 'contains', label: 'Contains' },
    { value: 'greater_than', label: 'Greater Than' },
    { value: 'less_than', label: 'Less Than' },
    { value: 'between', label: 'Between' },
    { value: 'in', label: 'In List' },
    { value: 'not_in', label: 'Not In List' }
  ];

  const entityFields = {
    compliance_violations: [
      { value: 'violation_type', label: 'Violation Type', type: 'text' },
      { value: 'severity', label: 'Severity', type: 'select', options: ['critical', 'high', 'medium', 'low'] },
      { value: 'department', label: 'Department', type: 'text' },
      { value: 'estimated_cost', label: 'Estimated Cost (Kina)', type: 'number' },
      { value: 'violation_status', label: 'Status', type: 'select', options: ['identified', 'pending_review', 'in_progress', 'completed'] }
    ],
    employees: [
      { value: 'department', label: 'Department', type: 'text' },
      { value: 'employment_type', label: 'Employment Type', type: 'select', options: ['full_time', 'casual', 'fixed_term', 'non_citizen'] },
      { value: 'citizen_status', label: 'Citizen Status', type: 'select', options: ['citizen', 'non_citizen'] },
      { value: 'is_active', label: 'Active Status', type: 'boolean' }
    ],
    compliance_reports: [
      { value: 'report_status', label: 'Report Status', type: 'select', options: ['draft', 'generated', 'reviewed', 'approved', 'submitted'] },
      { value: 'overall_compliance_score', label: 'Compliance Score', type: 'number' },
      { value: 'total_violations', label: 'Total Violations', type: 'number' }
    ]
  };

  const availableFields = entityFields?.[targetEntity] || [];

  const addFilter = () => {
    setFilters(prev => [
      ...prev,
      {
        id: Date.now(),
        field: availableFields?.[0]?.value || '',
        operator: 'equals',
        value: ''
      }
    ]);
  };

  const removeFilter = (filterId) => {
    setFilters(prev => prev?.filter(f => f?.id !== filterId));
  };

  const updateFilter = (filterId, updates) => {
    setFilters(prev => prev?.map(f => 
      f?.id === filterId ? { ...f, ...updates } : f
    ));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');

      if (!viewName?.trim()) {
        setError('Please enter a view name');
        return;
      }

      if (filters?.length === 0) {
        setError('Please add at least one filter');
        return;
      }

      const filterCriteria = filters?.reduce((acc, filter) => {
        acc[filter?.field] = {
          operator: filter?.operator,
          value: filter?.value
        };
        return acc;
      }, {});

      await dashboardCustomizationService?.createFilteredView({
        viewName: viewName?.trim(),
        viewDescription: viewDescription?.trim(),
        targetEntity,
        filterCriteria,
        isShared,
        createdBy: 'current_user@example.com' // Replace with actual user email
      });

      onSave?.();
      onClose?.();
    } catch (err) {
      setError(err?.message || 'Failed to save filtered view');
    } finally {
      setSaving(false);
    }
  };

  const getFieldConfig = (fieldValue) => {
    return availableFields?.find(f => f?.value === fieldValue);
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div>
            <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
              <Icon name="Filter" size={24} />
              Create Filtered View
            </h2>
            <p className="text-sm text-muted-foreground mt-1">
              Save custom filter combinations for quick access
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {error && (
            <div className="bg-error/10 border border-error text-error px-4 py-3 rounded-md flex items-start gap-2">
              <Icon name="AlertCircle" size={18} className="mt-0.5" />
              <span className="text-sm">{error}</span>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              View Name <span className="text-error">*</span>
            </label>
            <Input
              value={viewName}
              onChange={(e) => setViewName(e?.target?.value)}
              placeholder="e.g., Critical Violations Only"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
            </label>
            <Input
              value={viewDescription}
              onChange={(e) => setViewDescription(e?.target?.value)}
              placeholder="Optional description of this view"
            />
          </div>

          <div>
            <div className="flex items-center justify-between mb-3">
              <label className="block text-sm font-medium text-foreground">
                Filters <span className="text-error">*</span>
              </label>
              <Button
                variant="outline"
                size="sm"
                iconName="Plus"
                onClick={addFilter}
              >
                Add Filter
              </Button>
            </div>

            <div className="space-y-3">
              {filters?.map((filter) => {
                const fieldConfig = getFieldConfig(filter?.field);
                return (
                  <div key={filter?.id} className="bg-muted/30 rounded-lg p-4">
                    <div className="grid grid-cols-12 gap-3 items-start">
                      <div className="col-span-4">
                        <Select
                          value={filter?.field}
                          onChange={(e) => updateFilter(filter?.id, { field: e?.target?.value })}
                          options={availableFields?.map(f => ({ value: f?.value, label: f?.label }))}
                        />
                      </div>

                      <div className="col-span-3">
                        <Select
                          value={filter?.operator}
                          onChange={(e) => updateFilter(filter?.id, { operator: e?.target?.value })}
                          options={filterOperators}
                        />
                      </div>

                      <div className="col-span-4">
                        {fieldConfig?.type === 'select' ? (
                          <Select
                            value={filter?.value}
                            onChange={(e) => updateFilter(filter?.id, { value: e?.target?.value })}
                            options={fieldConfig?.options?.map(opt => ({ value: opt, label: opt }))}
                            placeholder="Select value"
                          />
                        ) : fieldConfig?.type === 'number' ? (
                          <Input
                            type="number"
                            value={filter?.value}
                            onChange={(e) => updateFilter(filter?.id, { value: e?.target?.value })}
                            placeholder="Enter value"
                          />
                        ) : (
                          <Input
                            value={filter?.value}
                            onChange={(e) => updateFilter(filter?.id, { value: e?.target?.value })}
                            placeholder="Enter value"
                          />
                        )}
                      </div>

                      <div className="col-span-1 flex justify-end">
                        <button
                          onClick={() => removeFilter(filter?.id)}
                          className="text-error hover:text-error/80 transition-colors"
                        >
                          <Icon name="Trash2" size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}

              {filters?.length === 0 && (
                <div className="text-center py-6 text-muted-foreground bg-muted/20 rounded-lg">
                  <Icon name="Filter" size={32} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">No filters added yet</p>
                  <p className="text-xs mt-1">Click "Add Filter" to create your first filter</p>
                </div>
              )}
            </div>
          </div>

          <div>
            <Checkbox
              checked={isShared}
              onChange={(e) => setIsShared(e?.target?.checked)}
              label="Share with team"
              description="Allow other users to access this filtered view"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border bg-muted/20">
          <Button
            variant="outline"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            iconName="Save"
            onClick={handleSave}
            loading={saving}
          >
            Save View
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FilteredViewCreator;